/**
 * EventThemeManager - Manages themed visual elements for seasonal events
 *
 * Provides themed invader configurations, colors, and visual overrides
 * based on the currently active seasonal event.
 */

import type { EventTheme, ThemedInvader, EventSoundPack } from './types';
import { INVADER_TYPES } from '../config/game.config';
import { eventManager } from './EventManager';

// Default game colors
const DEFAULT_COLORS = {
  backgroundColor: 0x000000,
  playerColor: 0x00ff00,
  playerBulletColor: 0x00ff00,
  alienBulletColor: 0xff0000,
  uiPrimaryColor: '#00ff00',
};

// Default march frequencies
const DEFAULT_MARCH_FREQUENCIES = [55, 49, 43.65, 38.89]; // A1, G1, F1, D#1

export class EventThemeManager {
  private static instance: EventThemeManager;
  private currentTheme: EventTheme | null = null;
  private currentSoundPack: EventSoundPack | null = null;

  private constructor() {
    // Subscribe to event changes
    eventManager.onEventChange((event) => {
      this.currentTheme = event?.theme || null;
      this.currentSoundPack = event?.soundPack || null;
    });
  }

  static getInstance(): EventThemeManager {
    if (!EventThemeManager.instance) {
      EventThemeManager.instance = new EventThemeManager();
    }
    return EventThemeManager.instance;
  }

  /**
   * Get the current theme (or null if no event)
   */
  getCurrentTheme(): EventTheme | null {
    return this.currentTheme;
  }

  /**
   * Get the current sound pack (or null if no event)
   */
  getCurrentSoundPack(): EventSoundPack | null {
    return this.currentSoundPack;
  }

  /**
   * Check if a themed event is active
   */
  hasActiveTheme(): boolean {
    return this.currentTheme !== null;
  }

  /**
   * Get the invader types to use (themed or default)
   */
  getInvaderTypes(): typeof INVADER_TYPES | ThemedInvader[] {
    if (this.currentTheme?.invaderTypes) {
      return this.currentTheme.invaderTypes;
    }
    return INVADER_TYPES;
  }

  /**
   * Get row type mapping for invaders
   * Returns array mapping row index to invader type index
   */
  getRowTypes(): number[] {
    // Default mapping: squid (0), crab (1), crab (1), octopus (2), octopus (2)
    return [0, 1, 1, 2, 2];
  }

  /**
   * Get a specific invader type by index
   */
  getInvaderType(index: number): typeof INVADER_TYPES[number] | ThemedInvader {
    const types = this.getInvaderTypes();
    return types[Math.min(index, types.length - 1)];
  }

  /**
   * Get invader type by key
   */
  getInvaderTypeByKey(key: string): typeof INVADER_TYPES[number] | ThemedInvader | undefined {
    const types = this.getInvaderTypes();
    return types.find((t) => t.key === key);
  }

  /**
   * Get background color
   */
  getBackgroundColor(): number {
    return this.currentTheme?.backgroundColor ?? DEFAULT_COLORS.backgroundColor;
  }

  /**
   * Get player color
   */
  getPlayerColor(): number {
    return this.currentTheme?.playerColor ?? DEFAULT_COLORS.playerColor;
  }

  /**
   * Get player bullet color
   */
  getPlayerBulletColor(): number {
    return this.currentTheme?.playerBulletColor ?? DEFAULT_COLORS.playerBulletColor;
  }

  /**
   * Get alien bullet color
   */
  getAlienBulletColor(): number {
    return this.currentTheme?.alienBulletColor ?? DEFAULT_COLORS.alienBulletColor;
  }

  /**
   * Get UI primary color (CSS hex string)
   */
  getUIPrimaryColor(): string {
    return this.currentTheme?.uiPrimaryColor ?? DEFAULT_COLORS.uiPrimaryColor;
  }

  /**
   * Get march sound frequencies (4 notes)
   */
  getMarchFrequencies(): number[] {
    return this.currentSoundPack?.marchFrequencies ?? DEFAULT_MARCH_FREQUENCIES;
  }

  /**
   * Get shoot sound frequency range [start, end]
   */
  getShootFrequencyRange(): [number, number] {
    return this.currentSoundPack?.shootFrequencyRange ?? [1200, 200];
  }

  /**
   * Get victory sound notes
   */
  getVictoryNotes(): number[] {
    return this.currentSoundPack?.victoryNotes ?? [261.63, 329.63, 392, 523.25]; // C4, E4, G4, C5
  }

  /**
   * Get game over sound notes
   */
  getGameOverNotes(): number[] {
    return this.currentSoundPack?.gameOverNotes ?? [220, 164.81, 110]; // A3, E3, A2
  }

  /**
   * Get theme name for display
   */
  getThemeName(): string {
    return this.currentTheme?.name ?? 'Classic';
  }

  /**
   * Get theme ID
   */
  getThemeId(): string {
    return this.currentTheme?.id ?? 'default';
  }

  /**
   * Convert hex number to CSS color string
   */
  static hexToColorString(hex: number): string {
    return '#' + hex.toString(16).padStart(6, '0');
  }

  /**
   * Convert CSS color string to hex number
   */
  static colorStringToHex(color: string): number {
    return parseInt(color.replace('#', ''), 16);
  }

  /**
   * Get all colors as an object for easy application
   */
  getAllColors(): typeof DEFAULT_COLORS {
    return {
      backgroundColor: this.getBackgroundColor(),
      playerColor: this.getPlayerColor(),
      playerBulletColor: this.getPlayerBulletColor(),
      alienBulletColor: this.getAlienBulletColor(),
      uiPrimaryColor: this.getUIPrimaryColor(),
    };
  }

  /**
   * Check if we should use themed sprites
   */
  useThemedSprites(): boolean {
    return this.hasActiveTheme() && this.currentTheme?.invaderTypes !== undefined;
  }

  /**
   * Get texture key prefix for current theme
   */
  getTextureKeyPrefix(): string {
    if (this.hasActiveTheme()) {
      return `event_${this.getThemeId()}_`;
    }
    return '';
  }
}

// Export singleton instance
export const eventThemeManager = EventThemeManager.getInstance();
